import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CircleCheck } from "lucide-react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { Button } from "@/components/ui/button";
import { SegmentRow } from "@/components/review/SegmentRow";

export default function PageReview() {
  const { pageId } = useParams();
  const { session } = useSession();
  const [page, setPage] = useState(null);

  const load = useCallback(() => api.get(`/pages/${pageId}`).then((r) => setPage(r.data)).catch((e) => toast.error(errMsg(e))), [pageId]);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, 6000);
    return () => clearInterval(t);
  }, [load]);

  const setReviewed = async () => {
    try {
      await api.patch(`/pages/${pageId}`, { reviewed: !page.reviewed });
      setPage({ ...page, reviewed: !page.reviewed });
      toast.success(page.reviewed ? "Marked as not reviewed" : "Marked as reviewed");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const updateSeg = (id, patch) => setPage((p) => ({ ...p, segments_list: p.segments_list.map((s) => (s.id === id ? { ...s, ...patch } : s)) }));

  if (!page) return <div className="font-mono text-sm text-slate-500" data-testid="page-loading">Loading page…</div>;
  const segs = page.segments_list;
  const flagged = segs.filter((s) => s.needs_check).length;

  return (
    <div className="space-y-6">
      <div>
        <Link to={`/sites/${page.site_id}`} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 transition-colors duration-150" data-testid="back-to-site"><ArrowLeft className="h-3.5 w-3.5" /> All pages</Link>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mt-3">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight font-mono" data-testid="page-path">{page.path}</h1>
            <div className="text-sm text-slate-500 mt-1">{segs.length} sentences{flagged ? ` · ${flagged} need checking` : ""}{page.priority ? " · re-checked by the top model" : ""}</div>
          </div>
          <Button onClick={setReviewed} disabled={!session.can_edit} data-testid="mark-reviewed-button" variant={page.reviewed ? "outline" : "default"} className={page.reviewed ? "" : "bg-emerald-600 hover:bg-emerald-700"}>
            <CircleCheck className="h-4 w-4 mr-1.5" /> {page.reviewed ? "Reviewed: undo" : "Mark page reviewed"}
          </Button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="segment-editor">
        <div className="hidden lg:grid grid-cols-2 text-[11px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
          <div className="px-5 py-2.5 border-r border-slate-200">English (source)</div>
          <div className="px-5 py-2.5">Español · click a sentence to edit</div>
        </div>
        {segs.length === 0 && <div className="p-8 text-sm text-slate-500">No text found on this page yet.</div>}
        {segs.map((s) => <SegmentRow key={s.id} seg={s} canEdit={session.can_edit} onSaved={updateSeg} />)}
      </div>
    </div>
  );
}
